<template>
  <div class="group-tabs">
    <v-tabs
      :value="tabIndex"
      show-arrows
      dense
      height="40"
      class="group-tabs__tabs"
      @change="onTabChange"
    >
      <v-tab class="text-none group-tabs__tab group-tabs__tab--all">
        All videos
        <span class="group-tabs__count ml-1">({{ totalCount }})</span>
      </v-tab>
      <v-tab
        v-for="group in groups"
        :key="group.id"
        class="text-none group-tabs__tab"
        :title="group.description || group.name"
      >
        <v-icon v-if="group.frozen_at" x-small class="mr-1" title="Frozen">mdi-snowflake</v-icon>
        {{ group.name }}
        <span class="group-tabs__count ml-1">({{ memberCount(group) }})</span>
      </v-tab>
      <v-btn
        text
        small
        color="primary"
        class="text-none align-self-center ml-2 group-tabs__new"
        :disabled="loading"
        @click="$emit('create-group')"
      >
        <v-icon small left>mdi-plus</v-icon>
        New group
      </v-btn>
    </v-tabs>

    <v-alert
      v-if="activeGroup"
      dense
      text
      color="primary"
      class="group-tabs__summary mt-2 mb-0"
      icon="mdi-flask-outline"
    >
      <div class="d-flex align-center flex-wrap">
        <span class="group-tabs__summary-text">{{ summaryText }}</span>
        <v-spacer />
        <v-btn
          small
          text
          color="primary"
          class="text-none group-tabs__add"
          @click="$emit('add-videos', activeGroup)"
        >
          <v-icon small left>mdi-plus-box-multiple-outline</v-icon>
          Add videos
        </v-btn>
        <v-btn
          small
          text
          color="primary"
          class="text-none group-tabs__rerun"
          :disabled="!memberCount(activeGroup)"
          @click="$emit('rerun-group', activeGroup)"
        >
          <v-icon small left>mdi-play-circle-outline</v-icon>
          Re-run group
        </v-btn>
        <v-btn
          small
          text
          color="primary"
          class="text-none group-tabs__open"
          @click="$emit('open-group', activeGroup)"
        >
          <v-icon small left>mdi-open-in-new</v-icon>
          Open group
        </v-btn>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { runStatusPresentation } from '@/utils/runLabels';

const STATUS_ORDER = ['SUCCEEDED', 'RUNNING', 'DISPATCHED', 'QUEUED', 'FAILED', 'CANCELLED'];

/**
 * 'All videos' + one tab per analysis group above the manual video list (M4 F13). `v-model` is
 * the selected group id (null = all). Under a group tab a one-line summary (config version,
 * engines, members, runs by status) offers Add videos / Re-run group / Open group.
 */
export default {
  name: 'GroupTabs',

  props: {
    /** Selected group id; null for 'All videos'. */
    value: { type: [Number, String], default: null },
    /** Groups from GET /analysis-groups (id, name, engines, config_version, members, runs_by_status). */
    groups: { type: Array, default: () => [] },
    /** Total videos shown under 'All videos'. */
    totalCount: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
  },

  computed: {
    tabIndex() {
      if (this.value == null) return 0;
      const index = this.groups.findIndex((group) => String(group.id) === String(this.value));
      return index === -1 ? 0 : index + 1;
    },
    activeGroup() {
      if (this.value == null) return null;
      return this.groups.find((group) => String(group.id) === String(this.value)) || null;
    },
    summaryText() {
      const group = this.activeGroup;
      if (!group) return '';
      const parts = [];
      if (group.config_version != null) parts.push(`config v${group.config_version}`);
      const engines = Array.isArray(group.engines) ? group.engines : [];
      if (engines.length) parts.push(engines.join(' / '));
      const members = this.memberCount(group);
      parts.push(`${members} ${members === 1 ? 'video' : 'videos'}`);
      const counts =
        group.runs_by_status && typeof group.runs_by_status === 'object'
          ? group.runs_by_status
          : {};
      STATUS_ORDER.forEach((status) => {
        const n = Number(counts[status] ?? counts[status.toLowerCase()] ?? 0);
        if (n > 0) parts.push(`${n} ${runStatusPresentation(status).label.toLowerCase()}`);
      });
      return parts.join(' · ');
    },
  },

  methods: {
    memberCount(group) {
      const raw = group?.members;
      if (Array.isArray(raw)) return raw.length;
      const n = Number(raw ?? group?.member_count ?? 0);
      return Number.isFinite(n) ? n : 0;
    },
    onTabChange(index) {
      const next = index > 0 && this.groups[index - 1] ? this.groups[index - 1].id : null;
      if (String(next ?? '') === String(this.value ?? '')) return;
      this.$emit('input', next);
    },
  },
};
</script>

<style scoped>
.group-tabs__count {
  opacity: 0.7;
  font-size: 0.75rem;
}

.group-tabs__summary-text {
  font-size: 0.8125rem;
}
</style>
